import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Map as MapIcon } from 'lucide-react';
import { givenName, type AssistantConversationDTO, type AssistantPendingDTO } from '@contracts/assistant';
import type { MissionCardStep, MissionCardValue } from '@contracts/assistant-missions';
import type { Id } from '@contracts/common';
import { ApiClientError, offlineError } from '../../api/client';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { lastTwoInitials as initials } from '../../lib/initials';
import { useNarrowView } from '../../lib/media';
import { presenceVar } from '../../lib/presence-color';
import { notify } from '../../lib/toast';
import { cn } from '../../lib/utils';
import { useAssistantOptional } from './AssistantProvider';
import { conversationKey, editAction, useOwnAppearance } from './api';
import { DRAFT_MAP_HREF } from './draft';
import { Hana } from './Hana';
import { PILL, PILL_TONE } from './ingest/BatchRows';
import { PendingWhy } from './PendingWhy';
import { afterText, doerLine, howSentence, MISSION_CARD } from './missions/labels';
import { storedSteps } from './usePendingDraft';
import { buildForest } from '../workplace/model';
import { ParentCombobox } from '../workplace/ParentPicker';
import { useBoard } from '../workplace/useBoard';
import { DEFAULT_PERSONA_NAME } from '@contracts/persona';

/**
 * The `mission` confirm card (S7 §6.2, D116, D117, D128; mockup §1): the plan Hana proposes, in the S5 batch card's
 * frame — the goal and its deadline, the person's brief verbatim (3 lines until opened), where the goal card goes, each
 * step with who does it, and how far Hana may go by herself. A step is unchecked or renamed here, kept on the card
 * until "Duyệt kế hoạch" sends it through the edit endpoint (D116 narrows only: a step once removed cannot come back,
 * so a box sent at each click could not be ticked again). A step's instruction is never shown (D140).
 */

const MUTED = 'text-[hsl(var(--h-muted-foreground))]';
const HEAD = `flex items-baseline justify-between gap-2 px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.04em] ${MUTED}`;
const HANA_PILL = 'bg-[hsl(var(--h-icon-bg-violet))] text-[hsl(var(--h-icon-violet))]';
/** A name edited in place: no border or ring of its own, focused or not (project rule); a muted fill says "editing". */
const NAME_FIELD = 'w-full min-w-0 rounded-sm border-0 bg-[hsl(var(--h-muted))] px-1 text-[hsl(var(--h-card-foreground))] '
  + 'outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0';
const EDITABLE = 'cursor-text rounded-sm underline decoration-[hsl(var(--h-muted-foreground))] decoration-dashed '
  + 'underline-offset-4 [overflow-wrap:anywhere] hover:text-[hsl(var(--h-primary))]';
/** The goal's name in the header reads as plain text (mockup §1); the dashed line shows on hover or focus. */
const QUIET = 'cursor-text rounded-sm decoration-dashed underline-offset-4 [overflow-wrap:anywhere] hover:underline focus-visible:underline';
const FAILED = 'Đã có lỗi xảy ra, vui lòng thử lại';

const ddMM = (date: string): string => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

/** A title shown as text, edited in place on a click: Enter or leaving keeps it, Escape puts it back. */
function InlineName({ value, label, readOnly, onChange, className, quiet = false }: {
  value: string; label: string; readOnly: boolean; onChange: (next: string) => void; className?: string; quiet?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  if (readOnly) return <span className={cn('[overflow-wrap:anywhere]', className)}>{value}</span>;
  if (draft === null) {
    return (
      // A span, not a <button> (BatchRows N2): a long title wraps as running text.
      <span role="button" tabIndex={0} className={cn(quiet ? QUIET : EDITABLE, className)} onClick={() => setDraft(value)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDraft(value); } }}>
        {value}
      </span>
    );
  }
  const keep = () => {
    const next = draft.trim();
    if (next !== '' && next !== value) onChange(next);
    setDraft(null);
  };
  return (
    <input aria-label={label} value={draft} autoFocus maxLength={200} className={cn(NAME_FIELD, className)}
      onChange={(e) => setDraft(e.target.value)} onBlur={keep}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); keep(); }
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setDraft(null); }
      }} />
  );
}

/**
 * The avatar of who does a step: the assistant's carry the owner's face with the owner's own persona figure at its
 * corner (S5 D7, S8 §6.2 — the card is only ever the owner's), a colleague's their own face.
 */
function Doer({ step, owner }: { step: MissionCardStep; owner: { id: Id; name: string } | null }) {
  const appearance = useOwnAppearance();
  const person = step.executor === 'other' ? step.assignee : owner;
  const peer = { '--peer': person === null ? 'var(--h-muted-foreground)' : presenceVar(person.id) } as CSSProperties;
  return (
    <span aria-hidden="true" style={peer}
      className="relative grid size-5 shrink-0 place-items-center rounded-full bg-[hsl(var(--peer))] text-[10px] font-bold text-[hsl(var(--h-presence-ink))]">
      {person === null ? '?' : initials(person.name)}
      {step.executor === 'hana' && (
        <span data-hana-badge=""
          className="absolute -bottom-1 -right-[6px] grid size-3.5 place-items-center rounded-full bg-[hsl(var(--h-card))] shadow-[0_0_0_1.5px_hsl(var(--h-card))]">
          {/* A 14 px badge, many on one card: always still. */}
          <Hana emotion="idle" size={14} still appearance={appearance} />
        </span>
      )}
    </span>
  );
}

type Place = { id: Id; title: string } | null;
const NO_ITEMS: never[] = [];

/**
 * "Đặt dưới · đổi" (D155): the Workplace's own parent picker over the one board cache. Mounted only while the popover
 * is open, so the chat reads no board until the owner asks to move the goal card; the root row is a new top-level goal.
 */
function PlacePicker({ current, onChoose }: { current: Id; onChoose: (place: Place) => void }) {
  const { board, status } = useBoard();
  const forest = useMemo(() => buildForest(board?.items ?? NO_ITEMS), [board]);
  if (board === undefined) {
    return <p className={cn('px-1 py-2 text-xs', MUTED)}>{status === 'error' ? FAILED : MISSION_CARD.loadingBoard}</p>;
  }
  return (
    <ParentCombobox forest={forest} label={MISSION_CARD.place} current={current} offerRoot
      onChoose={(id) => onChoose(id === null ? null : { id, title: forest.byId.get(id)?.item.title ?? '' })} />
  );
}

/** Steps in the card's order: each followed by the steps under it (`parentRef`), with its depth. */
function ordered(steps: readonly MissionCardStep[]): { step: MissionCardStep; depth: number }[] {
  const refs = new Set(steps.map((s) => s.ref));
  const out: { step: MissionCardStep; depth: number }[] = [];
  const seen = new Set<string>();
  const walk = (s: MissionCardStep, depth: number) => {
    if (seen.has(s.ref)) return;
    seen.add(s.ref);
    out.push({ step: s, depth });
    for (const c of steps) if (c.parentRef === s.ref) walk(c, depth + 1);
  };
  for (const s of steps) if (s.parentRef === null || !refs.has(s.parentRef)) walk(s, 0);
  return out;
}

/** A step and every step below it, by ref. */
function below(steps: readonly MissionCardStep[], ref: string): Set<string> {
  const out = new Set([ref]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const s of steps) if (s.parentRef !== null && out.has(s.parentRef) && !out.has(s.ref)) { out.add(s.ref); grew = true; }
  }
  return out;
}

/** The goal card's place: the parent's title on a chip, or "Thẻ gốc mới". */
function PlaceChip({ parent }: { parent: Place }) {
  if (parent === null) return <span className={MUTED}>{MISSION_CARD.newRoot}</span>;
  return (
    <span title={parent.title}
      className="inline-block min-w-0 max-w-full truncate rounded-[10px] bg-[hsl(var(--h-primary)/0.08)] px-2 py-px align-bottom text-xs font-semibold text-[hsl(var(--h-primary))]">
      {parent.title}
    </span>
  );
}

export function MissionCard({ pending, value, disabled, onAct, readOnly, className, onEdited, aside }: {
  pending: AssistantPendingDTO; value: MissionCardValue; disabled: boolean;
  onAct: (actionId: Id, kind: 'confirm' | 'reject') => void;
  /** A card shown where it is not the owner's to narrow (a run's, the MCP page): read and approve only. */
  readOnly: boolean; className?: string; onEdited?: (next: AssistantPendingDTO) => void; aside?: ReactNode;
}) {
  const appearance = useOwnAppearance();
  const assistant = useAssistantOptional();
  const queryClient = useQueryClient();
  const narrow = useNarrowView();
  const persona = assistant?.prefs.personaName ?? DEFAULT_PERSONA_NAME;
  const owner = assistant === null ? null : { id: assistant.principal.userId, name: assistant.principal.name };
  const [open, setOpen] = useState(false);
  /** Unchecked steps by ref; a step under an unchecked one is out with it. */
  const [off, setOff] = useState<ReadonlySet<string>>(() => new Set());
  const [names, setNames] = useState<ReadonlyMap<string, string>>(() => new Map());
  const [goal, setGoal] = useState<string | null>(null);
  /** Where the goal card goes, once the owner picked it here (`undefined`: as Hana proposed). */
  const [place, setPlace] = useState<Place | undefined>(undefined);
  const [picking, setPicking] = useState(false);
  const [log, setLog] = useState<Element | null>(null);
  const [saving, setSaving] = useState(false);

  const out = new Set<string>();
  for (const ref of off) for (const r of below(value.steps, ref)) out.add(r);
  const kept = value.steps.filter((s) => !out.has(s.ref));
  const refs = value.steps.map((s) => s.ref);
  const parent = place === undefined ? value.parent : place;
  const moved = (parent?.id ?? null) !== (value.parent?.id ?? null);
  const dirty = out.size > 0 || names.size > 0 || goal !== null || moved;
  const busy = disabled || saving;

  const toggle = (ref: string) => setOff((s) => {
    const next = new Set(s);
    if (next.has(ref)) next.delete(ref); else next.add(ref);
    return next;
  });

  const approve = async () => {
    if (busy || kept.length === 0) return;
    if (!dirty) { onAct(pending.actionId, 'confirm'); return; }
    // D116: the stored input echoed whole (parentId — omitted would mean "Thẻ gốc mới" — and the card value), less
    // the unchecked steps, with the new names; the server narrows to exactly that.
    const steps = storedSteps(pending.input).filter((s) => !out.has(s.ref))
      .map((s) => (names.has(s.ref) ? { ...s, title: names.get(s.ref)! } : s));
    // A move sends the picked card as `parentId`; a new top-level goal leaves it out, as the server reads it.
    const { parentId: stored, ...rest } = pending.input;
    const parentId = moved ? parent?.id : stored;
    const input = { ...rest, ...(parentId === undefined ? {} : { parentId }), ...(goal === null ? {} : { title: goal }), steps };
    setSaving(true);
    try {
      const next = await editAction(pending.actionId, { input }, crypto.randomUUID());
      const cid = assistant?.conversationId ?? null;
      if (cid !== null) {
        queryClient.setQueryData<AssistantConversationDTO>(conversationKey(cid), (c) => (c === undefined ? c
          : { ...c, pending: c.pending.map((p) => (p.actionId === pending.actionId ? next : p)) }));
      }
      onEdited?.(next);
      onAct(next.actionId, 'confirm');
    } catch (e) {
      const err = e instanceof ApiClientError ? e : offlineError();
      const field = err.fields === undefined ? undefined : Object.values(err.fields)[0];
      notify.error(field ?? (err.message || FAILED));
    } finally {
      setSaving(false);
    }
  };

  // "Sửa": what the card cannot change (who does a step, its date, the brief) is asked of Hana in the chat.
  const ask = () => {
    const box = document.querySelector<HTMLTextAreaElement>('[data-assistant-panel] textarea');
    box?.focus();
  };

  const how = howSentence(value.level, persona, value.runsPerDay, value.runTokenCap);
  const goalTitle = goal ?? value.title;

  return (
    <section role="group" aria-label={MISSION_CARD.heading} data-action={pending.actionId} data-mission-card=""
      className={cn('mr-auto w-full min-w-0 max-w-[92%] overflow-hidden rounded-[10px] border border-[hsl(var(--h-border))] bg-[hsl(var(--h-card))] text-sm', className)}>
      <div className="flex items-center gap-2 border-b border-[hsl(var(--h-border))] px-3 py-2.5">
        <Hana appearance={appearance} emotion="ask" size={24} still={assistant?.still ?? true} className="shrink-0" />
        <div className="min-w-0 flex-1">
          <b className="block font-bold text-[hsl(var(--h-card-foreground))]">{MISSION_CARD.heading}</b>
          <span className={cn('text-xs', MUTED)}>
            <InlineName value={goalTitle} label={MISSION_CARD.goalName} readOnly={readOnly} onChange={setGoal} quiet />
            {value.deadline !== null && ` · hạn ${ddMM(value.deadline)}`}
          </span>
        </div>
        <span className={cn(PILL, HANA_PILL)}>{MISSION_CARD.draft}</span>
      </div>
      {/* S6 §7.5, S7 §9.5: the taint line and why autonomy asked, as on every internal card (D226). */}
      <PendingWhy pending={pending} inset className="pb-0.5 pt-1" />

      <div role="group" aria-label={MISSION_CARD.brief}>
        <h4 className={HEAD}><span>{MISSION_CARD.brief}</span><span className="font-normal normal-case tracking-normal">{MISSION_CARD.verbatim}</span></h4>
        {/* The padding sits outside the clamped box: a clamp clips at its padding box, so its own bottom padding would
            show part of a fourth line. */}
        <div className="px-3 pb-2">
          <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}
            className={cn('w-full whitespace-pre-line text-left leading-normal text-[hsl(var(--h-card-foreground))] [overflow-wrap:anywhere]',
              open ? 'block' : 'line-clamp-3')}>
            {value.brief}
          </button>
        </div>
      </div>

      <div role="group" aria-label={MISSION_CARD.place}>
        <h4 className={HEAD}><span>{MISSION_CARD.place}</span></h4>
        <div className="px-3 pb-2">
          {readOnly ? <PlaceChip parent={parent} /> : (
            <Popover open={picking} onOpenChange={setPicking}>
              <PopoverTrigger asChild>
                <button ref={(el) => { setLog(el?.closest('[role="log"]') ?? null); }} type="button" disabled={busy}
                  aria-label={MISSION_CARD.changePlace}
                  className="group inline-flex min-h-11 max-w-full items-center gap-1 rounded-md text-left outline-none disabled:cursor-default lg:min-h-0">
                  <PlaceChip parent={parent} />
                  <span className={cn('shrink-0 text-xs group-hover:underline group-focus-visible:underline', MUTED)}>{MISSION_CARD.change}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent aria-label={MISSION_CARD.place} align="start"
                className="max-h-[var(--radix-popover-content-available-height)] w-80 max-w-[calc(100vw-32px)] overflow-y-auto p-2"
                collisionBoundary={log} collisionPadding={narrow ? { top: 8, right: 8, bottom: 84, left: 8 } : 8}
                onOpenAutoFocus={(e) => e.preventDefault()}>
                <PlacePicker current={parent?.id ?? ''} onChoose={(next) => { setPlace(next); setPicking(false); }} />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      <div role="group" aria-label={MISSION_CARD.steps}>
        <h4 className={HEAD}><span>{MISSION_CARD.steps}</span><span>{kept.length}</span></h4>
        <ul>
          {ordered(value.steps).map(({ step, depth }, i) => {
            const index = refs.indexOf(step.ref);
            const isOut = out.has(step.ref);
            const parentOut = step.parentRef !== null && out.has(step.parentRef);
            const title = names.get(step.ref) ?? step.title;
            const after = afterText(step.after, index, refs);
            return (
              <li key={step.ref} data-step={step.ref} style={{ paddingLeft: `${12 + depth * 18}px` }}
                className={cn('flex items-start gap-2 py-1.5 pr-3', i > 0 && 'border-t border-dashed border-[hsl(var(--h-border))]')}>
                {!readOnly && (
                  <label className="-my-1 flex min-h-11 min-w-11 shrink-0 cursor-pointer items-start justify-center pt-[7px] lg:min-h-0 lg:min-w-0 lg:pt-[3px]">
                    <input type="checkbox" aria-label={MISSION_CARD.pick(title)} checked={!isOut} disabled={parentOut || busy}
                      onChange={() => toggle(step.ref)}
                      className="size-4 cursor-pointer accent-[hsl(var(--h-primary))] disabled:cursor-default" />
                  </label>
                )}
                <div className={cn('min-w-0 flex-1 leading-normal', isOut && 'opacity-55')}>
                  <InlineName value={title} label={MISSION_CARD.stepName} readOnly={readOnly || isOut}
                    className={isOut ? 'line-through' : undefined}
                    onChange={(next) => setNames((m) => new Map(m).set(step.ref, next))} />
                  <div className={cn('mt-0.5 flex flex-wrap items-center gap-1.5 text-xs', MUTED)}>
                    <Doer step={step} owner={owner} />
                    <span>{doerLine(step, persona, givenName)}{after === null ? '' : ` ${after}`}</span>
                    {step.executor === 'other' && <span className={cn(PILL, PILL_TONE.update)}>{MISSION_CARD.other}</span>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div role="group" aria-label={MISSION_CARD.how(persona)}>
        <h4 className={HEAD}><span>{MISSION_CARD.how(persona)}</span></h4>
        <p data-how="" className={cn('px-3 pb-2.5 text-xs leading-normal', MUTED)}>
          {how.lead}<b className="font-bold">{how.level}</b>{how.rest}
        </p>
      </div>

      <div className="border-t border-[hsl(var(--h-border))] px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" disabled={busy || kept.length === 0} onClick={() => void approve()}>
            {MISSION_CARD.approve}
          </Button>
          {!readOnly && <Button type="button" size="sm" variant="outline" onClick={ask}>{MISSION_CARD.edit}</Button>}
          <Button type="button" size="sm" variant="outline" disabled={busy}
            onClick={() => onAct(pending.actionId, 'reject')}>{MISSION_CARD.cancel}</Button>
          <span className="flex-1" />
          {aside !== undefined && <span className={cn('text-xs', MUTED)}>{aside}</span>}
        </div>
        {/* Mockup §1: the hint on its own line under the buttons. */}
        {!readOnly && (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className={MUTED}>{MISSION_CARD.hint}</span>
            <Link to={DRAFT_MAP_HREF} onClick={() => { if (narrow) assistant?.setOpen(false); }}
              className="inline-flex min-h-11 items-center gap-1 rounded-md font-semibold text-[hsl(var(--h-primary))] hover:underline lg:min-h-0">
              <MapIcon className="size-3.5 shrink-0" aria-hidden="true" />Xem trên bản đồ
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
